import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, RefreshCw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { siteKeyRotate, siteKeys } from '@/shared/api/generated/delivery/delivery';
import type { EnvironmentBody, KeyBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { apiURL } from '@/shared/config';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { CopyButton } from '@/shared/ui/CopyButton';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import { Segmented } from '@/shared/ui/Segmented';
import { Skeleton } from '@/shared/ui/Skeleton';
import { toast } from '@/shared/ui/Toast';

import { envFiles } from './envFiles';
import styles from './SettingsPage.module.scss';

const dateFormat = new Intl.DateTimeFormat('ru', { dateStyle: 'medium', timeStyle: 'short' });

const NPMRC = '@pet-p-team:registry=https://npm.pkg.github.com';

const USAGE = `import { facetaFromEnv } from '@pet-p-team/faceta-sdk';

const faceta = facetaFromEnv();
const posts = await faceta.list('posts', { limit: 10 });`;

/**
 * «Подключение сайта»: всё, что нужно разработчику клиентского сайта, на
 * одной вкладке — ключи окружения, готовые .env под режимы запуска и шаги
 * от установки SDK до первого запроса.
 */
export function ConnectSection({
  siteId,
  environments,
  devOnly,
}: {
  siteId: string;
  environments: EnvironmentBody[];
  devOnly: boolean;
}) {
  // С сужением «только Development» ключей прода не видно.
  const available = environments.filter((item) => !devOnly || item.kind === 'development');
  const [kind, setKind] = useState<'production' | 'development'>(
    available.some((item) => item.kind === 'production') ? 'production' : 'development',
  );
  const environment = available.find((item) => item.kind === kind);

  return (
    <div className={styles['stack']}>
      {available.length > 1 && (
        <Segmented
          aria-label="Окружение"
          value={kind}
          onValueChange={(value) => {
            setKind(value === 'development' ? 'development' : 'production');
          }}
          options={[
            { value: 'production', label: 'Production' },
            { value: 'development', label: 'Development' },
          ]}
        />
      )}

      {environment !== undefined && (
        <EnvironmentKeys key={environment.id} siteId={siteId} environment={environment} />
      )}

      <section className={styles['card']}>
        <h3 className={styles['card__title']}>Как подключить сайт</h3>

        <ol className={styles['steps']}>
          <li>
            <span className={styles['steps__title']}>Поставьте SDK</span>
            <span className={styles['section__text']}>
              Пакет лежит в GitHub Packages. Добавьте в <code>.npmrc</code> сайта строку и
              установите пакет:
            </span>
            <Snippet text={NPMRC} />
            <Snippet text="npm i @pet-p-team/faceta-sdk" />
          </li>
          <li>
            <span className={styles['steps__title']}>Положите .env</span>
            <span className={styles['section__text']}>
              Скопируйте файлы выше в корень сайта: режим запуска выберет нужный — Development с
              черновиками, препрод или боевой сайт.
            </span>
          </li>
          <li>
            <span className={styles['steps__title']}>Сгенерируйте типы</span>
            <span className={styles['section__text']}>
              По схеме окружения — после каждого переноса схемы. Опечатка в имени коллекции или поля
              станет ошибкой компиляции, а не пустым местом на сайте.
            </span>
            <Snippet text="npx faceta-sdk types --out types/faceta.d.ts" />
          </li>
          <li>
            <span className={styles['steps__title']}>Забирайте содержимое</span>
            <Snippet text={USAGE} />
            <span className={styles['section__text']}>
              Для живого предпросмотра — <code>useFacetaPreview</code> и <code>FacetaBlocks</code>{' '}
              (React и Vue), адрес записи — в «Моделях», адрес препрода — на вкладке «Адреса».
            </span>
          </li>
        </ol>
      </section>
    </div>
  );
}

function EnvironmentKeys({
  siteId,
  environment,
}: {
  siteId: string;
  environment: EnvironmentBody;
}) {
  const queryClient = useQueryClient();
  const [rotating, setRotating] = useState<KeyBody['kind']>();
  const [shown, setShown] = useState(false);
  const queryKey = ['site-keys', siteId, environment.kind];

  const keys = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await siteKeys(siteId, environment.kind);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос ключей');
      }

      return response.data.items ?? [];
    },
  });

  const publicKey = keys.data?.find((key) => key.kind === 'public');
  const previewKey = keys.data?.find((key) => key.kind === 'preview');
  const files =
    publicKey === undefined || previewKey === undefined
      ? []
      : envFiles(environment.kind, apiURL(), publicKey.token, previewKey.token);
  const [fileName, setFileName] = useState<string>();
  const file = files.find((item) => item.name === fileName) ?? files[0];

  if (publicKey === undefined || previewKey === undefined || file === undefined) {
    return <Skeleton height={260} />;
  }

  const mask = (text: string) => (shown ? text : text.replace(previewKey.token, '•'.repeat(16)));

  return (
    <>
      <section className={styles['card']}>
        <h3 className={styles['card__title']}>
          <KeyRound />
          Ключи {environment.kind === 'production' ? 'Production' : 'Development'}
        </h3>

        <div className={styles['keys']}>
          <KeyRow
            title="Публичный ключ"
            badge="можно в браузер"
            hint="Только опубликованное. Из браузера пускается с адресов окружения."
            keyBody={publicKey}
            onRotate={() => {
              setRotating('public');
            }}
          />
          <KeyRow
            title="Ключ предпросмотра"
            badge="только сервер"
            hint="С черновиками. Запрос с ним из браузера отклоняется — держите его на сервере сайта."
            keyBody={previewKey}
            hidden={!shown}
            onToggle={() => {
              setShown((value) => !value);
            }}
            onRotate={() => {
              setRotating('preview');
            }}
          />
        </div>
      </section>

      <section className={styles['card']}>
        <div className={styles['card__head']}>
          <h3 className={styles['card__title']}>Файлы .env</h3>
          {files.length > 1 && (
            <Segmented
              aria-label="Режим запуска"
              value={file.name}
              onValueChange={setFileName}
              options={files.map((item) => ({ value: item.name, label: item.name }))}
            />
          )}
        </div>
        <p className={styles['section__text']}>
          Запуск: <code>{file.mode}</code>. <code>FACETA_KEY</code> — для сервера сайта,{' '}
          <code>FACETA_PUBLIC_KEY</code> можно отдать в браузер.
        </p>
        <Snippet text={file.text} shown={mask(file.text)} />
      </section>

      <ConfirmDialog
        open={rotating !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setRotating(undefined);
          }
        }}
        title={rotating === 'public' ? 'Заменить публичный ключ?' : 'Заменить ключ предпросмотра?'}
        description="Прежний ключ перестанет работать сразу: сайт с ним получит отказ, пока в его .env не поставят новый. Меняют ключ, если он мог утечь."
        confirmLabel="Заменить"
        tone="danger"
        onConfirm={async () => {
          if (rotating === undefined) {
            return;
          }

          try {
            await siteKeyRotate(siteId, environment.kind, rotating);
            toast.success('Ключ заменён', { description: 'Обновите .env сайта.' });
            void queryClient.invalidateQueries({ queryKey });
          } catch (cause) {
            toast.error('Не удалось заменить ключ', {
              description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
            });

            throw cause;
          }
        }}
      />
    </>
  );
}

function KeyRow({
  title,
  badge,
  hint,
  keyBody,
  hidden = false,
  onToggle,
  onRotate,
}: {
  title: string;
  badge: string;
  hint: string;
  keyBody: KeyBody;
  hidden?: boolean;
  onToggle?: () => void;
  onRotate: () => void;
}) {
  return (
    <div className={styles['key']}>
      <div className={styles['key__main']}>
        <span className={styles['key__title']}>
          {title}
          <Badge size="sm" tone={onToggle === undefined ? 'success' : 'warning'}>
            {badge}
          </Badge>
        </span>
        <code className={styles['key__token']}>
          {hidden ? `${keyBody.token.slice(0, 7)}${'•'.repeat(16)}` : keyBody.token}
        </code>
        <span className={styles['section__text']}>{hint}</span>
        <span className={styles['key__used']}>
          {keyBody.last_used_at === undefined ? (
            <>
              <TriangleAlert size={13} /> Ещё не использовался
            </>
          ) : (
            `Последний запрос: ${dateFormat.format(new Date(keyBody.last_used_at))}`
          )}
        </span>
      </div>
      <div className={styles['key__actions']}>
        {onToggle !== undefined && (
          <Button
            iconOnly
            size="sm"
            variant="ghost"
            tone="neutral"
            aria-label={hidden ? 'Показать ключ' : 'Спрятать ключ'}
            onClick={onToggle}
          >
            {hidden ? <Eye size={15} /> : <EyeOff size={15} />}
          </Button>
        )}
        <CopyButton value={keyBody.token} label={`Скопировать: ${title.toLowerCase()}`} />
        <Button
          iconOnly
          size="sm"
          variant="ghost"
          tone="neutral"
          aria-label={`Заменить: ${title.toLowerCase()}`}
          onClick={onRotate}
        >
          <RefreshCw size={15} />
        </Button>
      </div>
    </div>
  );
}

/** Код с кнопкой «Скопировать»; shown — что показать, если копируется другое (скрытый ключ). */
function Snippet({ text, shown }: { text: string; shown?: string }) {
  return (
    <div className={styles['snippet']}>
      <pre className={styles['snippet__text']}>{shown ?? text}</pre>
      <CopyButton value={text} label="Скопировать" className={styles['snippet__copy'] ?? ''} />
    </div>
  );
}
